import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play, X, Shield, Loader2, Car, Clock, MapPin, AlertTriangle,
  AlertCircle, Info, CheckCircle2, Smartphone, ChevronRight,
  ExternalLink, Zap, ShoppingBag, Route, Navigation, Gift,
} from "lucide-react";
import { API, axios } from "@/api";
import { BrandLogo } from "@/components/shared/BrandLogo";
import { toast } from "sonner";

const SEVERITY = {
  critical: { bg: "bg-red-50", border: "border-red-200", icon: AlertTriangle, color: "text-red-500", badge: "bg-red-500" },
  warning: { bg: "bg-amber-50", border: "border-amber-200", icon: AlertCircle, color: "text-amber-500", badge: "bg-amber-500" },
  info: { bg: "bg-sky-50", border: "border-sky-200", icon: Info, color: "text-sky-500", badge: "bg-sky-500" },
};

const ISSUE_ICONS = {
  not_signed_up: ExternalLink, lead_time: Clock, app_required: Smartphone,
  purchase_required: ShoppingBag, long_drive: Car, closing_time: Clock,
  unverified_offer: AlertCircle, unknown_value: Gift, no_verification_url: Info,
  window_mismatch: Clock, duplicate_business: AlertTriangle,
  too_many_stops_day: MapPin, slot_overcrowded: Clock,
  total_drive_too_long: Car, geographically_spread: Navigation,
  no_variety: Gift, only_one_stop: Gift, no_birthday_set: AlertTriangle,
};

function ReadinessRing({ pct, size = 72, stroke = 6 }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - pct / 100);
  const color = pct >= 80 ? "#10b981" : pct >= 50 ? "#f59e0b" : "#ef4444";
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color}
          strokeWidth={stroke} strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" className="transition-all duration-1000"
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-base font-black text-slate-800">{pct}%</span>
    </div>
  );
}

function StopCard({ stop, index, themeColor, onNavigateToOffer }) {
  const [expanded, setExpanded] = useState(false);
  const hasIssues = stop.issues?.length > 0;
  const critCount = stop.issues?.filter(i => i.severity === "critical").length || 0;
  const warnCount = stop.issues?.filter(i => i.severity === "warning").length || 0;

  return (
    <div data-testid={`rehearsal-stop-${stop.business_id}`}>
      {/* Drive segment */}
      {stop.drive_from_prev && (
        <div className="flex items-center gap-2 py-1.5 px-3 ml-5">
          <div className="w-0.5 h-5 bg-slate-200 rounded-full" />
          <Car className="w-3 h-3 text-slate-300" />
          <span className="text-[10px] text-slate-400">{stop.drive_from_prev.duration_min}min · {stop.drive_from_prev.distance_mi}mi</span>
        </div>
      )}

      <button
        onClick={() => hasIssues && setExpanded(!expanded)}
        className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
          critCount > 0 ? "bg-red-50/50 border-red-200" :
          warnCount > 0 ? "bg-amber-50/30 border-amber-200" :
          stop.claimed ? "bg-emerald-50/50 border-emerald-200" : "bg-white border-slate-100"
        } ${hasIssues ? "cursor-pointer hover:shadow-sm" : ""}`}
      >
        {/* Index badge */}
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-extrabold flex-shrink-0"
          style={{ background: stop.claimed ? "#10b981" : critCount > 0 ? "#ef4444" : warnCount > 0 ? "#f59e0b" : (themeColor || "#8b5cf6") }}
        >
          {stop.claimed ? <CheckCircle2 className="w-3.5 h-3.5" /> : index + 1}
        </div>

        {/* Stop info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-800 truncate">{stop.name}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-slate-400">{stop.time_slot}</span>
            <span className="text-[10px] text-slate-300">·</span>
            <span className="text-[10px] text-slate-400">~{stop.est_arrival}</span>
            {stop.est_visit_min && (
              <>
                <span className="text-[10px] text-slate-300">·</span>
                <span className="text-[10px] text-slate-400">{stop.est_visit_min}min visit</span>
              </>
            )}
          </div>
        </div>

        {/* Value + issue badges */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-sm font-bold text-emerald-600">${stop.value_est}</span>
          {hasIssues && (
            <div className="flex gap-0.5">
              {critCount > 0 && <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{critCount}</span>}
              {warnCount > 0 && <span className="w-5 h-5 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center">{warnCount}</span>}
            </div>
          )}
          {hasIssues && <ChevronRight className={`w-3.5 h-3.5 text-slate-300 transition-transform ${expanded ? "rotate-90" : ""}`} />}
        </div>
      </button>

      {/* Expanded issues */}
      <AnimatePresence>
        {expanded && stop.issues?.length > 0 && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="ml-10 mt-1 space-y-1.5 pb-1">
              {stop.issues.map((iss, j) => {
                const sev = SEVERITY[iss.severity] || SEVERITY.info;
                const IssIcon = ISSUE_ICONS[iss.type] || Info;
                return (
                  <div key={j} className={`flex items-start gap-2 p-2.5 rounded-lg ${sev.bg} ${sev.border} border`} data-testid={`rehearsal-issue-${stop.business_id}-${j}`}>
                    <IssIcon className={`w-3.5 h-3.5 ${sev.color} flex-shrink-0 mt-0.5`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] text-slate-700">{iss.message}</p>
                    </div>
                    {iss.action_type === "signup" && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onNavigateToOffer(iss.business_id); }}
                        className="text-[10px] font-bold text-sky-600 hover:text-sky-700 whitespace-nowrap flex items-center gap-0.5"
                      >
                        {iss.action_label} <ChevronRight className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function RehearseBirthday({ themeColor = "#8b5cf6" }) {
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(new Set());

  const runRehearsal = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/itinerary/rehearse`, {});
      setData(res.data);
      setActive(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to run rehearsal");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFixAll = useCallback(async () => {
    if (!data?.fixable_issues?.length) return;
    // Navigate to the first fixable issue's offer page
    const first = data.fixable_issues[0];
    if (first?.business_id) {
      navigate(`/offer/${first.business_id}`);
    }
    toast.info(`${data.fixable_count} issue${data.fixable_count !== 1 ? 's' : ''} need attention — start with the first one`);
  }, [data, navigate]);

  // Launch button
  if (!active) {
    return (
      <button
        onClick={runRehearsal}
        disabled={loading}
        className="w-full py-3.5 text-white text-sm font-bold rounded-xl disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg transition-all hover:shadow-xl"
        style={{ background: `linear-gradient(135deg, ${themeColor}, ${darken(themeColor, 40)})` }}
        data-testid="rehearse-birthday-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
        {loading ? "Simulating your birthday..." : "Rehearse Your Birthday"}
      </button>
    );
  }

  if (!data) return null;

  const { readiness_pct, issues_to_fix, route, stops, slot_warnings, fixable_count, itinerary_issues } = data;

  // Collect ALL issues from all stops into a flat list for the summary
  const allIssues = [];
  for (const stop of stops) {
    if (stop.issues?.length) {
      for (const iss of stop.issues) {
        allIssues.push({ ...iss, stop_name: stop.name, stop_business_id: stop.business_id });
      }
    }
  }
  // Add itinerary-level issues
  if (itinerary_issues?.length) {
    for (const iss of itinerary_issues) {
      allIssues.push(iss);
    }
  }

  // Filter out dismissed issues
  const activeIssues = allIssues.filter((_, i) => !dismissed.has(i));

  // Group stops by day + slot
  const grouped = {};
  for (const s of stops) {
    const key = `Day ${s.day} — ${s.time_slot}`;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(s);
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="rehearsal-mode">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4" style={{ color: themeColor }} />
          <span className="text-xs font-bold text-slate-800">Rehearsal Mode</span>
        </div>
        <button
          onClick={() => setActive(false)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-500 text-[11px] font-semibold hover:bg-slate-200 transition-colors"
          data-testid="exit-rehearsal-btn"
        >
          <X className="w-3.5 h-3.5" /> Exit
        </button>
      </div>

      {/* Readiness hero */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm" data-testid="rehearsal-readiness">
        <div className="flex items-center gap-4">
          <ReadinessRing pct={readiness_pct} />
          <div className="flex-1">
            <p className="text-base font-extrabold text-slate-800">
              Your birthday is {readiness_pct}% ready
            </p>
            {issues_to_fix > 0 ? (
              <p className="text-xs text-slate-500 mt-0.5">
                {activeIssues.filter(i => i.severity === "critical").length > 0 &&
                  <span className="text-red-500 font-semibold">{activeIssues.filter(i => i.severity === "critical").length} critical</span>}
                {activeIssues.filter(i => i.severity === "critical").length > 0 && activeIssues.filter(i => i.severity === "warning").length > 0 && ", "}
                {activeIssues.filter(i => i.severity === "warning").length > 0 &&
                  <span className="text-amber-500 font-semibold">{activeIssues.filter(i => i.severity === "warning").length} warning{activeIssues.filter(i => i.severity === "warning").length !== 1 ? "s" : ""}</span>}
                {activeIssues.length > 0 ? " — scroll down for details" : " — all addressed!"}
              </p>
            ) : (
              <p className="text-sm text-emerald-600 font-semibold mt-0.5">All clear!</p>
            )}
          </div>
        </div>

        {/* Route stats strip */}
        <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
          <div className="flex-1 flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-sky-50">
            <Car className="w-3.5 h-3.5 text-sky-500" />
            <div>
              <p className="text-xs font-bold text-sky-700">{route.total_drive_time_min}min</p>
              <p className="text-[9px] text-sky-500">drive time</p>
            </div>
          </div>
          <div className="flex-1 flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-amber-50">
            <Route className="w-3.5 h-3.5 text-amber-500" />
            <div>
              <p className="text-xs font-bold text-amber-700">{route.total_distance_mi}mi</p>
              <p className="text-[9px] text-amber-500">total distance</p>
            </div>
          </div>
          <div className="flex-1 flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-emerald-50">
            <Gift className="w-3.5 h-3.5 text-emerald-500" />
            <div>
              <p className="text-xs font-bold text-emerald-700">${data.total_value}</p>
              <p className="text-[9px] text-emerald-500">{data.total_stops} stops</p>
            </div>
          </div>
        </div>
      </div>

      {/* Slot warnings */}
      {slot_warnings?.length > 0 && (
        <div className="space-y-1.5">
          {slot_warnings.map((w, i) => (
            <div key={i} className="flex items-center gap-2 p-2.5 rounded-xl bg-amber-50 border border-amber-200">
              <AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
              <p className="text-[11px] text-amber-700">{w.message}</p>
            </div>
          ))}
        </div>
      )}

      {/* ═══ Issues Breakdown — what needs attention and why ═══ */}
      {activeIssues.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="issues-breakdown">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500" />
            <span className="text-sm font-bold text-slate-800">What needs your attention</span>
            <span className="ml-auto text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{activeIssues.length}</span>
          </div>
          <div className="divide-y divide-slate-100">
            {activeIssues.map((iss, idx) => {
              const realIdx = allIssues.indexOf(iss);
              const sev = SEVERITY[iss.severity] || SEVERITY.info;
              const IssIcon = ISSUE_ICONS[iss.type] || Info;
              return (
                <div key={realIdx} className="px-4 py-3" data-testid={`issue-detail-${realIdx}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${sev.bg}`}>
                      <IssIcon className={`w-3.5 h-3.5 ${sev.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-bold text-slate-700">{iss.stop_name || "Your itinerary"}</p>
                      <p className="text-[11px] text-slate-600 mt-0.5">{iss.message}</p>
                      {iss.suggestion && (
                        <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">{iss.suggestion}</p>
                      )}
                    </div>
                  </div>
                  {/* Action buttons based on type */}
                  <div className="flex gap-2 mt-2 ml-10">
                    {(iss.action_type === "signup" || iss.action_type === "app_download") && iss.business_id && (
                      <button
                        onClick={() => navigate(`/offer/${iss.business_id || iss.stop_business_id}`)}
                        className="text-[10px] font-bold text-white px-3 py-1.5 rounded-lg"
                        style={{ background: themeColor }}
                        data-testid={`fix-issue-${realIdx}`}
                      >
                        {iss.action_label || "Fix"}
                      </button>
                    )}
                    {iss.action_type === "edit_itinerary" && (
                      <button
                        onClick={() => { onClose(); toast.info("Scroll down to your itinerary to rearrange stops"); }}
                        className="text-[10px] font-bold text-white px-3 py-1.5 rounded-lg bg-indigo-500"
                        data-testid={`edit-issue-${realIdx}`}
                      >
                        {iss.action_label || "Edit itinerary"}
                      </button>
                    )}
                    {iss.action_type === "browse_deals" && (
                      <button
                        onClick={() => navigate("/")}
                        className="text-[10px] font-bold text-white px-3 py-1.5 rounded-lg bg-emerald-500"
                        data-testid={`browse-issue-${realIdx}`}
                      >
                        {iss.action_label || "Browse deals"}
                      </button>
                    )}
                    {iss.action_type === "edit_profile" && (
                      <button
                        onClick={() => navigate("/profile")}
                        className="text-[10px] font-bold text-white px-3 py-1.5 rounded-lg bg-violet-500"
                        data-testid={`profile-issue-${realIdx}`}
                      >
                        {iss.action_label || "Go to profile"}
                      </button>
                    )}
                    {iss.action_type === "dismiss" && (
                      <button
                        onClick={() => setDismissed(prev => new Set([...prev, realIdx]))}
                        className="text-[10px] font-semibold text-slate-500 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200"
                        data-testid={`dismiss-issue-${realIdx}`}
                      >
                        {iss.action_label || "I'm okay with this"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* All issues addressed */}
      {allIssues.length > 0 && activeIssues.length === 0 && (
        <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-4 flex items-center gap-3" data-testid="all-issues-addressed">
          <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-bold text-emerald-700">All issues addressed!</p>
            <p className="text-xs text-emerald-500">You've reviewed everything. You're ready for your birthday.</p>
          </div>
        </div>
      )}

      {/* Stop-by-stop rehearsal */}
      <div className="space-y-1" data-testid="rehearsal-stops">
        {Object.entries(grouped).map(([label, groupStops]) => (
          <div key={label}>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-1 pt-2 pb-1">{label}</p>
            <div className="space-y-1">
              {groupStops.map((stop, i) => (
                <StopCard
                  key={stop.id || stop.business_id}
                  stop={stop}
                  index={i}
                  themeColor={themeColor}
                  onNavigateToOffer={(bid) => navigate(`/offer/${bid}`)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Re-run button */}
      <div className="space-y-2 pt-2">
        <button
          onClick={runRehearsal}
          disabled={loading}
          className="w-full py-2.5 border-2 border-slate-200 text-slate-600 text-xs font-bold rounded-xl hover:bg-slate-50 flex items-center justify-center gap-1.5 disabled:opacity-50"
          data-testid="rerun-rehearsal-btn"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" style={{ color: themeColor }} />}
          Re-run Rehearsal
        </button>
      </div>
    </motion.div>
  );
}

function darken(hex, amount = 30) {
  if (!hex || hex.length < 7) return hex;
  const r = Math.max(0, parseInt(hex.slice(1, 3), 16) - amount);
  const g = Math.max(0, parseInt(hex.slice(3, 5), 16) - amount);
  const b = Math.max(0, parseInt(hex.slice(5, 7), 16) - amount);
  return `rgb(${r},${g},${b})`;
}
